export type ApiRecord = Record<string, unknown>;

export type Property = {
  id: string;
  groupKey?: string;
  title: string;
  source?: string;
  status?: string;
  researchStatus?: string;
  queueStatus?: string;
  location?: string;
  address?: string;
  price?: number | null;
  area?: number | null;
  rooms?: number | null;
  url?: string;
  discoveredAt?: string;
  updatedAt?: string;
  versionId?: string;
  raw: ApiRecord;
};

export type ResearchRun = {
  id: string;
  listingId?: string;
  modelIdentifier?: string;
  status: string;
  createdAt?: string;
  approvedAt?: string;
  output?: ApiRecord;
  assessment?: ApiRecord;
  raw: ApiRecord;
};

export type Settings = {
  version?: number;
  financialDefaults?: ApiRecord;
  evaluationProfile?: ApiRecord;
  raw: ApiRecord;
};

export const asRecord = (value: unknown): ApiRecord => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as ApiRecord : {};
export const asArray = (value: unknown): unknown[] => Array.isArray(value) ? value : [];
export const stringValue = (record: ApiRecord, ...keys: string[]): string | undefined => {
  for (const key of keys) { const value = record[key]; if (typeof value === 'string' && value.trim()) return value; }
  return undefined;
};
export const numberValue = (record: ApiRecord, ...keys: string[]): number | null => {
  for (const key of keys) { const value = record[key]; if (typeof value === 'number' && Number.isFinite(value)) return value; if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value); }
  return null;
};

export function parseProperty(value: unknown): Property {
  const raw = asRecord(value);
  const listing = asRecord(raw.listing ?? raw.latestListing);
  const latestVersion = asRecord(raw.latestVersion ?? raw.version);
  const facts = asRecord(raw.facts);
  // IDs and portal links belong to the stable listing; commercial facts belong to its latest version.
  const source = { ...raw, ...listing, ...latestVersion, facts };
  const id = stringValue(listing, 'id', 'listingId', 'listing_id') ?? stringValue(source, 'listingId', 'id', 'listing_id') ?? '';
  return {
    id,
    groupKey: stringValue(source, 'propertyGroupKey', 'groupKey'),
    title: stringValue(source, 'title', 'name', 'headline') ?? 'Ohne Titel',
    source: stringValue(source, 'source', 'portal'),
    status: stringValue(source, 'status', 'listingStatus'),
    researchStatus: stringValue(raw, 'researchStatus'),
    queueStatus: stringValue(raw, 'queueStatus'),
    location: stringValue(source, 'location', 'city', 'municipality'),
    address: stringValue(source, 'address', 'streetAddress'),
    price: numberValue(source, 'purchasePrice', 'price', 'priceEur'),
    area: numberValue(source, 'livingAreaSqm', 'livingAreaM2', 'areaM2', 'livingArea', 'area'),
    rooms: numberValue(source, 'rooms', 'roomCount'),
    url: stringValue(source, 'canonicalUrl', 'url', 'originalUrl', 'portalUrl', 'listingUrl'),
    discoveredAt: stringValue(source, 'discoveredAt', 'createdAt', 'firstSeenAt'),
    updatedAt: stringValue(source, 'updatedAt', 'lastSeenAt'),
    versionId: stringValue(source, 'versionId', 'latestVersionId'),
    raw
  };
}

export function parseRun(value: unknown): ResearchRun {
  const raw = asRecord(value);
  return { id: stringValue(raw, 'id', 'runId') ?? '', listingId: stringValue(raw, 'listingId'), modelIdentifier: stringValue(raw, 'modelIdentifier', 'model'), status: stringValue(raw, 'status') ?? 'unknown', createdAt: stringValue(raw, 'createdAt'), approvedAt: stringValue(raw, 'approvedAt'), output: asRecord(raw.structuredData ?? raw.output ?? raw.result), assessment: asRecord(raw.assessment), raw };
}

export const eur = (value: number | null | undefined) => value === null || value === undefined ? '–' : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
export const number = (value: number | null | undefined, suffix = '') => value === null || value === undefined ? '–' : `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(value)}${suffix}`;
